import { useState, useRef, useEffect, type ComponentProps } from 'react'
import ReactDatePicker from 'react-datepicker'
import { faClose } from '@fortawesome/pro-regular-svg-icons'
import type { Middleware } from '@floating-ui/dom'

import { QUARTER_PNL_OPTIONS, YEARLY_PNL_OPTIONS } from '@/constants'

import { Button } from '../../atoms/button'
import { Icon } from '../../atoms/icon'
import { Text } from '../../atoms/text'
import { Dialog } from '../dialog'
import { DatePickerInput } from './input'
import { TypographyStyles, TextColors } from '../../../utils/style-helpers'
import { Select } from '../../atoms/select'
import { convertSelectedDateToString } from './helpers'

interface IMobileDatePickerProps extends Omit<
  ComponentProps<typeof ReactDatePicker>,
  'onChange' | 'selectsRange'
> {
  onChange: (dates: [string, string]) => void
  dateFormat: string
  className?: string
  selectsRange: true
  showMonthPicker?: boolean
  showYearPicker?: boolean
  isMobile?: boolean
  onClear?: () => void
  handleQuickOption?: (option: {
    value: string | number
    startDate: string
    endDate: string
  }) => void
  onClearClick?: () => void
  popperModifiers?: Middleware[]
}

export const MobileDatePicker = ({
  onChange,
  dateFormat,
  className = '',
  selectsRange,
  showMonthPicker,
  showYearPicker,
  isMobile = false,
  onClearClick,
  handleQuickOption,
  popperModifiers,
  ...props
}: IMobileDatePickerProps) => {
  const [openModal, setOpenModal] = useState(false)
  const [range, setRange] = useState<[Date | undefined, Date | undefined]>([
    undefined,
    undefined,
  ])
  const [selectedYearlyOption, setSelectedYearlyOption] = useState<
    number | undefined
  >()
  const [selectedQuarterOption, setSelectedQuarterOption] = useState<
    number | undefined
  >()
  const pickerRef = useRef<ReactDatePicker>(null)

  const [start, end] = range
  const fallbackRange = range
  const selectedDate = start
  const dateValue = range
    .filter(Boolean)
    .map((date) => (date ? convertSelectedDateToString(date, dateFormat) : ''))
    .join(' - ')

  useEffect(() => {
    pickerRef.current?.setOpen(openModal)
  }, [openModal])

  const handleDateChange = (
    dateOrRange: Date | [Date | null, Date | null] | null
  ) => {
    if (Array.isArray(dateOrRange)) {
      setRange([dateOrRange[0] || undefined, dateOrRange[1] || undefined])
    }
  }

  const handleClear = () => {
    if (onClearClick) onClearClick()
    setRange([undefined, undefined])
  }

  const handleCancel = () => {
    setRange(fallbackRange)
    setOpenModal(false)
  }

  const handleApply = () => {
    onChange([
      start ? convertSelectedDateToString(start, dateFormat) : '',
      end ? convertSelectedDateToString(end, dateFormat) : '',
    ])

    setOpenModal(false)
  }

  return (
    <div>
      <DatePickerInput
        {...props}
        dateFormat={dateFormat}
        onClearClick={handleClear}
        onClick={() => setOpenModal(true)}
        onChange={() => {}}
        selectsRange={selectsRange}
        value={dateValue}
      />

      <Dialog open={openModal} onOpenChange={setOpenModal}>
        <Dialog.Content
          className={`${className} mobile-date-picker fullscreen`}
        >
          <ReactDatePicker
            {...(() => {
              // Remove conflicting props that might be passed in
              const {
                showMonthYearDropdown: _showMonthYearDropdown,
                ...safeProps
              } = props as Record<string, unknown>
              return safeProps
            })()}
            selectsRange
            selectsMultiple={undefined}
            dateFormat={dateFormat}
            disabledKeyboardNavigation
            endDate={end}
            focusSelectedMonth
            monthsShown={1}
            onChange={handleDateChange}
            ref={pickerRef}
            open={openModal}
            selected={selectedDate}
            shouldCloseOnSelect={!selectsRange || Boolean(showYearPicker)}
            showMonthYearPicker={showMonthPicker}
            showYearPicker={showYearPicker}
            startDate={start}
            value={dateValue}
            useWeekdaysShort
            popperModifiers={popperModifiers}
            isClearable
          >
            {selectsRange && (
              <>
                <div className="left-panel">
                  {isMobile && (
                    <Dialog.Close asChild>
                      <div>
                        <Icon icon={faClose} className="cursor-pointer" />
                      </div>
                    </Dialog.Close>
                  )}

                  <div>
                    <Text
                      variant={TypographyStyles.EYEBROW}
                      className="mb-2"
                      color={TextColors.MOSS}
                    >
                      By Year
                    </Text>

                    <Select<number>
                      placeholder="Select"
                      options={YEARLY_PNL_OPTIONS.map((option, index) => ({
                        text: String(option.value),
                        value: index,
                      }))}
                      onValueChange={(index) => {
                        setSelectedYearlyOption(index)
                        handleQuickOption?.(YEARLY_PNL_OPTIONS[index])
                      }}
                      value={selectedYearlyOption}
                    />
                  </div>

                  <div>
                    <Text
                      variant={TypographyStyles.EYEBROW}
                      className="mb-2"
                      color={TextColors.MOSS}
                    >
                      By Quarter
                    </Text>

                    <Select<number>
                      placeholder="Select"
                      options={QUARTER_PNL_OPTIONS.map((option, index) => ({
                        text: String(option.value),
                        value: index,
                      }))}
                      onValueChange={(index) => {
                        setSelectedQuarterOption(index)
                        handleQuickOption?.(QUARTER_PNL_OPTIONS[index])
                      }}
                      value={selectedQuarterOption}
                    />
                  </div>
                </div>

                <Dialog.Footer className="datepicker-ctas">
                  <Dialog.Close asChild>
                    <Button
                      className="cancel"
                      onClick={handleCancel}
                      size="lg"
                      variant="link"
                    >
                      Cancel
                    </Button>
                  </Dialog.Close>

                  <Button onClick={handleApply} size="lg" variant="primary">
                    Apply
                  </Button>
                </Dialog.Footer>
              </>
            )}
          </ReactDatePicker>
        </Dialog.Content>
      </Dialog>
    </div>
  )
}
